/** One public/config request for the HTML boot script and every first mount. */

const inflight = new Map();

export function publicConfigUrlsMatch(left, right) {
  const a = String(left || '').trim();
  const b = String(right || '').trim();
  if (!a || !b) return false;
  if (a === b) return true;
  try {
    const base = 'http://player.local';
    return new URL(a, base).pathname === new URL(b, base).pathname;
  } catch {
    return false;
  }
}

function abortedError() {
  const err = new Error('The operation was aborted.');
  err.name = 'AbortError';
  return err;
}

function adoptBootPromise(boot, url, unwrap) {
  if (!boot || !boot.promise || typeof boot.promise.then !== 'function') return null;
  if (boot.adopted) return null;
  if (!publicConfigUrlsMatch(boot.url, url)) return null;
  boot.adopted = true;
  return Promise.resolve(boot.promise).then((payload) => (unwrap ? unwrap(payload) : payload));
}

/**
 * Join the boot request when it is for this URL. Concurrent callers share it.
 * A later call, after that request settles, loads again.
 * A caller abort does not cancel the shared request.
 */
export function loadPublicConfigOnce({ url, load, boot, unwrap, signal } = {}) {
  const key = String(url || '').trim();
  if (!key) return Promise.reject(new Error('Missing public config URL.'));
  if (signal && signal.aborted) return Promise.reject(abortedError());

  for (const pending of inflight.values()) {
    if (publicConfigUrlsMatch(pending.url, key)) return pending.promise;
  }

  const bootPromise = adoptBootPromise(boot, key, unwrap);
  const run = bootPromise
    ? bootPromise.catch(() => load(key))
    : Promise.resolve().then(() => load(key));

  let shared;
  shared = run.catch((err) => {
    const current = inflight.get(key);
    if (current && current.promise === shared) inflight.delete(key);
    throw err;
  });
  inflight.set(key, { url: key, promise: shared });
  shared.finally(() => {
    const current = inflight.get(key);
    if (current && current.promise === shared) inflight.delete(key);
  });
  return shared;
}

export function __resetPublicConfigOnceForTests() {
  inflight.clear();
}
